import React, { FC } from 'react';
import Lozenge from '@atlaskit/lozenge';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import { token } from '@atlaskit/tokens';
import { SloDTO } from '@atlassiansox/phobos-client-ts';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { useNavigate, Link } from 'react-router-dom';

import {
  tableRowsStyles,
  tableRowsCozyPaddingStyles,
  tableHeadStyles,
  flexStart,
  spaceGapMinimum,
  textTransformCapitalize,
  colors,
  spaceGapTiny,
  textOverflowHidden,
} from '@/styles';
import { makeSLOGraph } from '@/controllers/slo-graph-controller';
import { PerformanceWithEvents } from '@/ui';

import {
  formatErrorBudget,
  formatObjectiveResult,
  formatPerformanceResult,
  getErrorBudget,
  getErrorBudgetNumberColorStyles,
  getHeroNumberColorStyles,
} from '../../slo-card/utils';
import { getIndicatorCohortMemberName, getIndicatorRollupCohorts } from '../utils';

import {
  cohortMemberRowsStyles,
  cohortMemberRowStyles,
  actionStyles,
  tagStyles,
  percentSignStyles,
  secondaryTextStyles,
  primaryTextStyles,
  messageStyles,
} from './styles';

type SLOCohortMembersProps = {
  slos: SloDTO[];
};

const SLOCohortMembers: FC<SLOCohortMembersProps> = ({ slos }) => {
  const navigate = useNavigate();

  return (
    <div>
      <div css={[tableHeadStyles, cohortMemberRowStyles]}>
        <div>Cohort Member</div>
        <div>Error Budget</div>
        <div>
          <div css={[flexStart, spaceGapMinimum]}>
            <span>SLI</span>
            <img alt="/" width={9} height={9} src="/static/slash.svg" />
            <span>SLO</span>
            <Lozenge>
              <span css={textTransformCapitalize}>28 days</span>
            </Lozenge>
          </div>
        </div>
        <div>Rollup Cohorts</div>
        <div>&nbsp;</div>
        <div>&nbsp;</div>
      </div>
      <ul css={[tableRowsStyles, tableRowsCozyPaddingStyles, cohortMemberRowsStyles]}>
        {slos.map(slo => {
          const { indicator, objective, performance, publicId, quality, type } = slo;
          const name: string = getIndicatorCohortMemberName(indicator);
          const cohorts: string[] = getIndicatorRollupCohorts(indicator);

          const { error } = makeSLOGraph(
            objective,
            indicator,
            performance as PerformanceWithEvents,
            publicId,
            quality,
            type
          );

          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              key={publicId}
              css={[cohortMemberRowStyles]}
              onClick={() => {
                // All the links are opened in SPA. If the user wants to open
                // the link in a new tab, they can use "Cmd" + click to open in
                // a new tab. This is to be consistent with how jira, confluence
                // and opsgenie links work.
                // window.open(`/slo/${encodeURIComponent(publicId)}`, '_blank');
                // publicId e.g: ceca6987-d5bb-42a5-a4d1-d8f3339d9e6b:shard:jira-prod-us-mc-2
                navigate(`/slo/${encodeURIComponent(publicId)}`);
              }}
            >
              <div>
                <div css={tagStyles}>
                  <Lozenge>{name}</Lozenge>
                </div>
              </div>
              <div>
                <div css={[flexStart, spaceGapMinimum]}>
                  <span
                    css={[
                      primaryTextStyles,
                      getErrorBudgetNumberColorStyles({
                        performance,
                        objective,
                      }),
                    ]}
                  >
                    {formatErrorBudget(getErrorBudget(performance?.result, objective))}
                  </span>
                  <span css={percentSignStyles}>%</span>
                </div>
              </div>
              <div>
                <div css={[flexStart, spaceGapMinimum]}>
                  <span
                    css={[
                      primaryTextStyles,
                      getHeroNumberColorStyles({
                        performance,
                        objective,
                      }),
                    ]}
                  >
                    {formatPerformanceResult(performance?.result)}
                  </span>
                  <img alt="/" width={9} height={9} src="/static/slash.svg" />
                  <span css={secondaryTextStyles}>{formatObjectiveResult(objective)}</span>
                  <span css={[percentSignStyles]}>%</span>
                </div>
              </div>
              <div>
                {cohorts && (
                  <div css={[flexStart, spaceGapTiny]}>
                    {cohorts.map((cohort: string, index: number) => (
                      <span key={cohort} css={[flexStart, spaceGapTiny, tagStyles]}>
                        <Lozenge>{cohort}</Lozenge>
                        {index !== cohorts.length - 1 && (
                          <ChevronRightLargeIcon
                            size="small"
                            primaryColor={token('color.text.subtlest', colors.N100)}
                            label=""
                          />
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* <div>{isDraft && <Lozenge appearance="moved">draft</Lozenge>}</div> */}
              <div>
                {error && (
                  <Lozenge appearance="moved" maxWidth={250}>
                    <div css={[flexStart, messageStyles, spaceGapMinimum]}>
                      <WarningIcon
                        size="small"
                        primaryColor={token('color.icon.warning', colors.Y300)}
                        label="warning-icon"
                      />
                      <span css={textOverflowHidden}>{error.title}</span>
                    </div>
                  </Lozenge>
                )}
              </div>
              <div css={actionStyles}>
                <Link relative="path" to={`/slo/${encodeURIComponent(publicId)}`}>
                  View SLO
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SLOCohortMembers;
